import React, { Fragment, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import useAsyncCallback from "../../hooks/useAsyncCallback";
import ShiftLogTimeSelector from "./shift_log_time_selector";
import ClientSearchAutoComplete from "../clients/client_search_autocomplete";
import { useTableViewContext } from "./TableViewContext";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { RefreshShiftLogsContext } from "./RefreshShiftLogsContext";
import ShiftLogSeenCheckbox from "./shift_log_seen_checkbox";
import ShiftLogFormHospice from "./shift_log_form_hospice";
import { SHIFT_LOG_TASK_FIELDS } from "../../constants";

function ShiftLogForm() {
  const { state, dispatch } = useTableViewContext();
  const [hospiceView, setHospiceView] = useState("");
  const [newPhotos, setNewPhotos] = useState([]);
  const [removePhotoIds, setRemovePhotoIds] = useState([]);

  const [showClientSearchBox, setShowClientSearchBox] = useState(false);
  const isNewForm = state.action === "new";

  const [clientAutocompletedId, setClientAutocompletedId] = useState(state.selectedClientId);
  const { pageParams } = state;

  const handleSeenChange = (newIsSeen) => {
    setFormValues((prevFormValues) => ({
      ...prevFormValues,
      seen: newIsSeen
    }));
  };

  const refreshShiftLog = useContext(RefreshShiftLogsContext);
  const { loading, data: shiftLog } = useAsyncCallback(async () => {
    if (isNewForm) {
      return await getNewShiftLog();
    }
    return await getShiftLogById(state.selectedShiftLog.id);
  });
  useEffect(() => {
    if (!loading && shiftLog) {
      if (isNewForm) {
        setFormValues({
          ...shiftLog,
          shift_num: state.selectedRowShiftNum
        });
      } else {
        setFormValues(shiftLog);
      }
      setNewPhotos([]);
      setRemovePhotoIds([]);
    }
  }, [loading]);
  const [formValues, setFormValues] = useState(null);

  const handleSubmit = async (event) => {
    try {
      event.preventDefault();
      // Check if any required input or textarea is not filled
      const form = document.getElementById("shift-log-popup-form");
      const requiredInputs = form.querySelectorAll("input[required], textarea[required]");
      const emptyFields = [];

      requiredInputs.forEach((input) => {
        if (!input.value.trim()) {
          emptyFields.push(
            input.name
              .replace("_text", "")
              .split("_")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ")
          );
        }
      });

      if (emptyFields.length > 0) {
        for (const field of emptyFields) {
          toastr.error(`Please fill in the required field: ${field}`);
        }
        return;
      }

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
      const url = isNewForm ? "/shift_logs.json" : `/shift_logs/${state.selectedShiftLog.id}.json`;
      const method = isNewForm ? "POST" : "PATCH";
      await axios({
        method: method,
        url: url,
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "application/json"
        },
        data: buildShiftLogFormData({
          formValues,
          newPhotos,
          removePhotoIds,
          client_autocomplete_id: clientAutocompletedId,
          date: pageParams.date || state.selectedShiftLog?.log_date || state.selectedDateForNewLog,
          location:
            pageParams.location ||
            state.selectedShiftLog?.location ||
            state.selectedClientDetail.location,
          client: state.selectedClientName
        })
      });
      await refreshShiftLog();

      dispatch({ type: "reset" });
    } catch (error) {
      const result = error?.response?.data;
      if (result && result.base) {
        const errorMessages = result.base.join(", ");
        toastr.error(errorMessages || "Something went wrong");
      } else if (result && result.message) {
        toastr.error(result.message || "Something went wrong");
      } else if (result) {
        const errorMessages = Object.values(result).flat().join(", ");
        toastr.error(errorMessages || "Something went wrong");
      } else {
        toastr.error("Something went wrong");
      }
    }
  };

  if (loading || !formValues) {
    return <div>loading...</div>;
  }

  const existingPhotos = formValues.shift_log_photos || [];
  const keptExistingPhotos = existingPhotos.filter((photo) => !removePhotoIds.includes(photo.id));
  const remainingPhotoSlots = MAX_SHIFT_LOG_PHOTOS - keptExistingPhotos.length;
  const canAddMorePhotos = remainingPhotoSlots > newPhotos.length;

  const handlePhotoSelection = (event) => {
    const selectedPhotos = Array.from(event.target.files || []);
    if (selectedPhotos.length + newPhotos.length > remainingPhotoSlots) {
      toastr.error(`A shift note can only have ${MAX_SHIFT_LOG_PHOTOS} photos.`);
    }
    setNewPhotos([...newPhotos, ...selectedPhotos].slice(0, remainingPhotoSlots));
    event.target.value = "";
  };

  return (
    <div className="shift-log-form-popup container">
      <h1 className="title" style={{ color: "grey" }}>
        {formValues.client_name || state.selectedClientName}
      </h1>
      <div className="shift-log-form-popup-header">
        <h1 className="title" style={{ color: "black" }}>
          {isNewForm ? "New Shift Log: " : "Edit Shift Log:"}
        </h1>
        {shiftLog.location === "Diane Morrison Hospice" && (
          <HospiceViewOptions hospiceView={hospiceView} setHospiceView={setHospiceView} />
        )}
      </div>

      <form
        id="shift-log-popup-form"
        className="form-box"
        style={{
          marginTop: "0px"
        }}
        onSubmit={handleSubmit}
      >
        <div className="shift-log-page">
          <div className="client-action">
            {isNewForm && (
              <>
                <label className="label time-title">Time</label>
                <div className="time-select">
                  <ShiftLogTimeSelector
                    hour={formValues.hour}
                    minute={formValues.minute}
                    am_pm={formValues.am_pm}
                  />
                </div>
                {!state.selectedClientName && (
                  <>
                    <label className="label check-title">
                      Please fill in if associated to client
                    </label>
                    <ClientSearchAutoComplete
                      input_field_name="shift_log[client_name]"
                      selected_client_id={clientAutocompletedId}
                      set_selected_client_id={setClientAutocompletedId}
                      className="is-rounded"
                    />
                    <div className="check-desc">
                      <span className="form-autocomplete-note">
                        Choose registered clients from dropdown
                      </span>
                    </div>
                  </>
                )}
              </>
            )}
            {!isNewForm && (
              <>
                <label className="label time-title">Time of incident</label>
                <div className="time-select">
                  <ShiftLogTimeSelector
                    hour={formValues.hour}
                    minute={formValues.minute}
                    am_pm={formValues.am_pm}
                  />
                </div>
                {/* ToDo: Updating client doesn't work. Disabling and adding it in the list of tasks */}
                {false && (
                  <>
                    <label className="label check-title">Check to update client</label>
                    <div className="check-select">
                      <input
                        type="checkbox"
                        name="client_name_checked"
                        value={showClientSearchBox}
                        onChange={() => setShowClientSearchBox(!showClientSearchBox)}
                        className="checkbox shift-logs-client-update-checkbox client-associate"
                      />
                      {showClientSearchBox && (
                        <div className="client-search-bar match-client">
                          <ClientSearchAutoComplete
                            name="shift_log[client_name]"
                            selected_client_id={clientAutocompletedId}
                            set_selected_client_id={setClientAutocompletedId}
                            className="is-rounded"
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}
                {showClientSearchBox && (
                  <div className="check-desc">
                    <span className="form-autocomplete-note match-client">
                      Choose registered clients from dropdown
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="shift-num-form">
            <label htmlFor="shift_num">Shift Number:</label>
            <select
              name="shift_num"
              value={formValues.shift_num}
              onChange={(e) => {
                setFormValues({ ...formValues, shift_num: e.target.value });
              }}
              className="time-select"
            >
              {shiftNumOptions.map((shiftNumOption, index) => {
                return (
                  <option key={index} value={shiftNumOption}>
                    {shiftNumOption}
                  </option>
                );
              })}
            </select>
          </div>
          {shiftLog.location === "Diane Morrison Hospice" && (
            <div className="log-rows-category">
              <h3>Hygiene</h3>
            </div>
          )}
          <div className="log-rows-form">
            {SHIFT_LOG_TASK_FIELDS.map((shiftLogFormField, index) => (
              <Fragment key={`${index} form fields`}>
                <label className="checkbox_container">
                  <input
                    type="checkbox"
                    name={shiftLogFormField.value}
                    className="checkbox"
                    checked={formValues[shiftLogFormField.value]}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setFormValues({
                        ...formValues,
                        [shiftLogFormField.value]: isChecked,
                        [`${shiftLogFormField.value}_text`]: isChecked
                          ? formValues[`${shiftLogFormField.value}_text`]
                          : ""
                      });
                    }}
                  />
                  {shiftLogFormField.label}
                </label>

                <div className="input_container">
                  {formValues[shiftLogFormField.value] && (
                    <textarea
                      required
                      name={`${shiftLogFormField.value}_text`}
                      placeholder={`${shiftLogFormField.label} Notes`}
                      value={formValues[`${shiftLogFormField.value}_text`]}
                      onChange={(e) => {
                        setFormValues({
                          ...formValues,
                          [`${shiftLogFormField.value}_text`]: e.target.value
                        });
                      }}
                      className="input shift-log-note-field"
                    />
                  )}
                </div>
              </Fragment>
            ))}
          </div>
          {shiftLog.location === "Diane Morrison Hospice" && (
            <ShiftLogFormHospice
              formValues={formValues}
              setFormValues={setFormValues}
              shiftLog={shiftLog}
              hospiceView={hospiceView}
            />
          )}

          <div className="med-check">
            <div>Meds Given As Prescribed?</div>
            <div>
              <input
                type="checkbox"
                name="medication"
                className="checkbox med-true"
                checked={formValues.medication}
                onChange={(e) => {
                  setFormValues({
                    ...formValues,
                    medication: e.target.checked,
                    medication_text: ""
                  });
                }}
              />
              Yes
            </div>
            <div>
              <input
                type="checkbox"
                name="medication"
                className="checkbox med-false"
                checked={!formValues.medication}
                onChange={(e) => {
                  setFormValues({
                    ...formValues,
                    medication: !e.target.checked,
                    medication_text: ""
                  });
                }}
              />
              No
            </div>
            {!formValues.medication && (
              <input
                type="text"
                name="medication_text"
                placeholder="Why Not?"
                value={formValues.medication_text}
                onChange={(e) => {
                  setFormValues({ ...formValues, medication_text: e.target.value });
                }}
                className="input med-text"
              />
            )}
          </div>
          <div>
            <ShiftLogSeenCheckbox isSeen={formValues.seen} onChange={handleSeenChange} />
          </div>
          <label className="label">What happened?</label>
          <textarea
            name="log_description"
            value={formValues.log_description}
            onChange={(e) => {
              setFormValues({ ...formValues, log_description: e.target.value });
            }}
            className="input client-text"
          />

          <div className="shift-log-photos-field">
            <label className="label">Photos</label>
            <p className="shift-log-photos-field__hint">
              Add up to {MAX_SHIFT_LOG_PHOTOS} photos.
            </p>
            {existingPhotos.length > 0 && (
              <div className="shift-log-photo-grid">
                {existingPhotos.map((photo) => (
                  <div className="shift-log-photo" key={photo.id}>
                    <a href={photo.url} target="_blank" rel="noopener noreferrer">
                      <img
                        src={photo.url}
                        alt={photo.filename}
                        className="shift-log-photo__image"
                      />
                    </a>
                    <label className="checkbox shift-log-photo__remove">
                      <input
                        type="checkbox"
                        checked={removePhotoIds.includes(photo.id)}
                        onChange={(e) => {
                          setRemovePhotoIds(
                            e.target.checked
                              ? [...removePhotoIds, photo.id]
                              : removePhotoIds.filter((photoId) => photoId !== photo.id)
                          );
                        }}
                      />
                      Remove
                    </label>
                  </div>
                ))}
              </div>
            )}
            {newPhotos.length > 0 && (
              <div className="shift-log-selected-photos">
                {newPhotos.map((photo, index) => (
                  <button
                    className="button is-light is-small"
                    key={`${photo.name}-${index}`}
                    type="button"
                    onClick={() => {
                      setNewPhotos(newPhotos.filter((_, photoIndex) => photoIndex !== index));
                    }}
                  >
                    {photo.name} x
                  </button>
                ))}
              </div>
            )}
            <input
              type="file"
              name="shift_log[photos][]"
              accept="image/*"
              multiple
              disabled={!canAddMorePhotos}
              onChange={handlePhotoSelection}
              className="input shift-log-photos-field__input"
            />
          </div>

          <div className="shift-log-action">
            {isNewForm && (
              <button type="submit" className="button is-primary" onClick={handleSubmit}>
                Create Shift Log
              </button>
            )}
            {!isNewForm && (
              <button type="submit" className="button is-primary" onClick={handleSubmit}>
                Update Shift Log
              </button>
            )}
            <button
              type="button"
              className="button is-light"
              onClick={() => {
                dispatch({
                  type: "reset"
                });
              }}
            >
              Close
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default ShiftLogForm;

const shiftNumOptions = ["1st", "2nd", "3rd"];
const MAX_SHIFT_LOG_PHOTOS = 3;

function buildShiftLogFormData({
  formValues,
  newPhotos,
  removePhotoIds,
  client_autocomplete_id,
  date,
  location,
  client
}) {
  const data = new FormData();
  Object.entries(formValues).forEach(([key, value]) => {
    if (key === "shift_log_photos" || value === null || value === undefined) return;

    data.append(`shift_log[${key}]`, value);
  });
  newPhotos.forEach((photo) => data.append("shift_log[photos][]", photo));
  removePhotoIds.forEach((photoId) => data.append("remove_shift_log_photo_ids[]", photoId));
  data.append("client_autocomplete_id", client_autocomplete_id || "");
  data.append("date", date || "");
  data.append("location", location || "");
  data.append("client", client || "");

  return data;
}

async function getShiftLogById(shiftLogId) {
  const response = await axios.get(`/shift_logs/${shiftLogId}.json`);
  return response.data;
}

async function getNewShiftLog() {
  const response = await axios.get("/shift_logs/new.json");
  return response.data;
}

const HospiceViewOptions = ({ hospiceView, setHospiceView }) => {
  const handleDefaultClick = (e) => {
    e.preventDefault();
    setHospiceView("");
  };

  const handleOriginalClick = (e) => {
    e.preventDefault();
    setHospiceView("original");
  };

  return (
    <div className="">
      <a href="#" onClick={handleDefaultClick}>
        <button
          id="switch_default_view_btn"
          className={`button ${hospiceView !== "original" ? "is-active" : ""}`}
        >
          Default
        </button>
      </a>
      <a href="#" onClick={handleOriginalClick}>
        <button
          id="switch_original_view_btn"
          className={`button ${hospiceView === "original" ? "is-active" : ""}`}
        >
          Original
        </button>
      </a>
    </div>
  );
};

HospiceViewOptions.propTypes = {
  hospiceView: PropTypes.string,
  setHospiceView: PropTypes.func
};
